import { useEffect, useState } from "react";
import api, { getErrorMessage } from "@/lib/api";
import { Card } from "@/components/studio/primitives";

export const dollars = (cents) =>
  (Number(cents || 0) / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

export function InvoiceStatusPill({ status }) {
  return <span className="rounded-full border border-[var(--studio-divider)] px-3 py-1 text-xs capitalize text-[var(--text-secondary)]">{status}</span>;
}

/** Retained accounting history. There are no invoice-authoring actions. */
export default function TuitionHistory({ client = api, endpoint = "/billing/invoices", compact = false }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    client.get(endpoint)
      .then(({ data }) => { if (active) setInvoices(data.invoices || []); })
      .catch((err) => { if (active) setError(getErrorMessage(err, "Could not load invoice history.")); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [client, endpoint]);

  if (compact && !loading && !error && invoices.length === 0) return null;
  return (
    <div className="space-y-6" data-testid="tuition-history">
      {!compact && <h1 className="font-serif text-2xl text-[var(--text-primary)]">Invoice history</h1>}
      <Card padding={24} className="space-y-4">
        <p className="text-sm text-[var(--text-secondary)]">
          Tuition invoicing is no longer available in Hans. Your previous invoices remain here.
        </p>
        {loading ? <p className="text-sm text-[var(--text-secondary)]">Loading invoice history…</p>
          : error ? <p role="alert" className="text-sm text-[var(--state-danger)]">{error}</p>
          : invoices.length === 0 ? <p className="text-sm text-[var(--text-secondary)]">No previous invoices.</p>
          : <ul className="divide-y divide-[var(--studio-divider)]">
            {(compact ? invoices.slice(0, 3) : invoices).map((invoice) => (
              <li key={invoice.invoice_id} className="flex flex-wrap items-center gap-4 py-4" data-testid={`invoice-${invoice.invoice_id}`}>
                <span className="min-w-0 flex-1 text-sm text-[var(--text-primary)]">{invoice.student_name || "Invoice"}</span>
                <span className="font-mono text-sm text-[var(--text-primary)]">{dollars(invoice.amount_cents)}</span>
                <span className="text-xs text-[var(--text-secondary)]">{invoice.due_date ? `Due ${invoice.due_date}` : ""}</span>
                <InvoiceStatusPill status={invoice.status} />
              </li>
            ))}
          </ul>}
      </Card>
    </div>
  );
}
